import React, { useState, useEffect, useMemo, useRef } from 'react';
import Fuse from 'fuse.js';
import { useProjectStore, usePoleStore, useCableStore } from '../../stores';
import { TopologyView } from '../Topology/TopologyView';
import { CassetteDocumentation } from './CassetteDocumentation';
import { TechnicalDocumentation } from './TechnicalDocumentation';
import { ProjectDeliverable } from './ProjectDeliverable';
import { InstallationChecklistOverview } from './InstallationChecklistOverview';
import { TechnicalReportDialog } from '../Dialogs/TechnicalReportDialog';
import { EditPoleDialog } from '../Dialogs/EditPoleDialog';
import { TasksView } from '../Tasks/TasksView';
import { SplitterBranches } from './SplitterBranches';
import { Pole } from '../../types';
import './DocumentationView.css';
import { poleTypeLabel } from '../../utils/poleLabels';

type ViewMode = 'topology' | 'pole-list' | 'branches' | 'pole-detail' | 'technical-doc' | 'project-deliverable' | 'tasks' | 'installation-checklist';

export const DocumentationView: React.FC = () => {
  const { currentProject } = useProjectStore();
  const { poles, fetchPoles } = usePoleStore();
  const { cables, fetchCables } = useCableStore();
  const [viewMode, setViewMode] = useState<ViewMode>('pole-list');
  const [selectedPole, setSelectedPole] = useState<Pole | null>(null);
  const [showTechnicalReport, setShowTechnicalReport] = useState(false);
  const [reportPole, setReportPole] = useState<Pole | null>(null);
  const [showEditPoleDialog, setShowEditPoleDialog] = useState(false);
  const [photoPole, setPhotoPole] = useState<Pole | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Na telefóne je sedem záložiek širších než displej a posúvajú sa prstom.
  // Keď je zapnutá tá vpravo, musí sa sama ukázať — inak technik vidí pruh
  // so zvýraznenou záložkou mimo obrazovky a nevie, kde je.
  const zalozkyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const aktivna = zalozkyRef.current?.querySelector('button.active') as HTMLElement | null;
    aktivna?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [viewMode]);

  useEffect(() => {
    if (currentProject) {
      fetchPoles(currentProject.id);
      fetchCables(currentProject.id);
    }
  }, [currentProject, fetchPoles, fetchCables]);

  // Natural sort function for pole names (Stĺpik_1, Stĺpik_2, ..., Stĺpik_10, ...)
  const naturalSort = (a: Pole, b: Pole): number => {
    const nameA = a.name || '';
    const nameB = b.name || '';

    // Split into parts: text and numbers
    const partsA = nameA.match(/(\d+|\D+)/g) || [];
    const partsB = nameB.match(/(\d+|\D+)/g) || [];

    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
      const partA = partsA[i] || '';
      const partB = partsB[i] || '';

      // If both parts are numbers, compare numerically
      const numA = parseInt(partA, 10);
      const numB = parseInt(partB, 10);

      if (!isNaN(numA) && !isNaN(numB)) {
        if (numA !== numB) return numA - numB;
      } else {
        // Otherwise compare as strings
        if (partA !== partB) return partA.localeCompare(partB);
      }
    }

    return 0;
  };

  // Filter poles with cassettes, exclude virtual poles, and sort naturally
  const polesWithCassettes = poles
    .filter((p) => p.cassette?.has_cassette && !p.is_virtual)
    .sort(naturalSort);

  // Configure Fuse.js for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(polesWithCassettes, {
      keys: [
        { name: 'name', weight: 2 },
        { name: 'pole_type', weight: 1 },
        { name: 'notes', weight: 0.5 },
        { name: 'latitude', weight: 0.3 },
        { name: 'longitude', weight: 0.3 },
      ],
      threshold: 0.4,
      includeScore: true,
      minMatchCharLength: 1,
    });
  }, [polesWithCassettes]);

  // Filter poles based on search query and sort naturally
  const filteredPoles = useMemo(() => {
    if (!searchQuery.trim()) {
      return polesWithCassettes;
    }
    const results = fuse.search(searchQuery);
    return results.map(result => result.item).sort(naturalSort);
  }, [polesWithCassettes, searchQuery, fuse]);

  const handleExportPDF = () => {
    window.print();
  };

  const handleExportExcel = () => {
    // TODO: Implement Excel export
    alert('Export do Excelu zatiaľ nie je hotový.');
  };

  const handleOpenTechnicalReport = (pole: Pole, e: React.MouseEvent) => {
    e.stopPropagation();
    setReportPole(pole);
    setShowTechnicalReport(true);
  };

  const handleCloseTechnicalReport = () => {
    setShowTechnicalReport(false);
    setReportPole(null);
  };

  const handleOpenPhotos = (pole: Pole, e: React.MouseEvent) => {
    e.stopPropagation();
    setPhotoPole(pole);
    setShowEditPoleDialog(true);
  };

  const handleClosePhotos = () => {
    setShowEditPoleDialog(false);
    setPhotoPole(null);
  };

  return (
    <div className="documentation-view">
      <div className="documentation-header">
        <div className="view-mode-selector" ref={zalozkyRef}>
          <button
            className={viewMode === 'topology' ? 'active' : ''}
            onClick={() => setViewMode('topology')}
          >
            🌐 Topológia
          </button>
          <button
            className={viewMode === 'pole-list' ? 'active' : ''}
            onClick={() => setViewMode('pole-list')}
          >
            📋 Zoznam stĺpikov
          </button>
          <button
            className={viewMode === 'branches' ? 'active' : ''}
            onClick={() => setViewMode('branches')}
          >
            🌿 Vetvy
          </button>
          <button
            className={viewMode === 'installation-checklist' ? 'active' : ''}
            onClick={() => setViewMode('installation-checklist')}
          >
            📋 Montážny zoznam
          </button>
          <button
            className={viewMode === 'tasks' ? 'active' : ''}
            onClick={() => setViewMode('tasks')}
          >
            ✅ Úlohy
          </button>
          <button
            className={viewMode === 'technical-doc' ? 'active' : ''}
            onClick={() => setViewMode('technical-doc')}
          >
            📋 Technická dokumentácia
          </button>
          <button
            className={viewMode === 'project-deliverable' ? 'active' : ''}
            onClick={() => setViewMode('project-deliverable')}
          >
            📊 Výstupná dokumentácia
          </button>
        </div>
      </div>

      <div className="documentation-content">
        {viewMode === 'topology' && <TopologyView />}

        {viewMode === 'technical-doc' && <TechnicalDocumentation />}

        {viewMode === 'project-deliverable' && <ProjectDeliverable />}

        {viewMode === 'installation-checklist' && <InstallationChecklistOverview />}

        {viewMode === 'tasks' && <TasksView />}

        {viewMode === 'branches' && (
          <SplitterBranches
            poles={poles}
            cables={cables}
            onOpenPole={(p) => {
              setSelectedPole(p);
              setViewMode('pole-detail');
            }}
          />
        )}

        {viewMode === 'pole-list' && (
          <div className="pole-list-view">
            <div className="pole-list-header">
              <h2>Stĺpiky s kazetou ({filteredPoles.length}/{polesWithCassettes.length})</h2>
              <p>Kliknutím na stĺpik zobrazíte podrobnú dokumentáciu</p>
            </div>

            {/* Search Input */}
            <div className="pole-search-container">
              <input
                type="text"
                placeholder="🔍 Hľadať stĺpik (názov, typ, GPS, poznámky)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pole-search-input-doc"
              />
              {searchQuery && (
                <button
                  className="pole-search-clear-doc"
                  onClick={() => setSearchQuery('')}
                  title="Vymazať"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="pole-grid">
              {filteredPoles.map((pole) => {
                // V kazete sú DVA druhy splitterov a miešať ich do jedného čísla
                // bolo mätúce: „Zákazníci 0/24" na stĺpe, kde žiadny zákaznícky
                // splitter nie je, len rozvodný 1:16 pre ďalšie káble.
                //   • rozvodný (krabica) — vedie káble ďalej do siete
                //   • zákaznícky (is_customer_splitter) — na ňom visia ľudia;
                //     býva na virtuálnom dvojčati stĺpa
                // V produkcii to sedí na 100 %: splittery s is_customer_splitter
                // nesú všetkých 137 zákazníkov, ostatné ani jedného.
                const zdroje = [pole, ...poles.filter(
                  (x) => x.parent_pole_id === pole.id && x.is_virtual)];
                const vsetkySplittery = zdroje.flatMap(
                  (z) => (z.cassette?.splitters || []) as any[]);

                const rozvodne = vsetkySplittery.filter(
                  (sp: any) => sp?.is_customer_splitter !== true);
                const zakaznicke = vsetkySplittery.filter(
                  (sp: any) => sp?.is_customer_splitter === true);

                const vystupyRozvodne = rozvodne.flatMap(
                  (sp: any) => (sp.outputs || []) as any[]);
                const obsadeneRozvodne = vystupyRozvodne.filter(
                  (o: any) => o?.cable_id || o?.customer_name).length;

                const vystupy = zakaznicke.flatMap(
                  (sp: any) => (sp.outputs || []) as any[]);
                const obsadenych = vystupy.filter((o: any) => o?.customer_name).length;

                // Farba nadradeného splittra — podľa nej sa krabica odlíši
                const farbaKrabice = rozvodne.find((sp: any) => sp?.color)?.color || null;
                const typyKrabice = Array.from(
                  new Set(rozvodne.map((sp: any) => sp?.type).filter(Boolean))).join(' + ');
                return (
                <div
                  key={pole.id}
                  className="pole-card"
                  style={farbaKrabice ? { borderLeft: `5px solid ${farbaKrabice}` } : undefined}
                  onClick={() => {
                    setSelectedPole(pole);
                    setViewMode('pole-detail');
                  }}
                >
                  <div className="pole-card-header">
                    <h3>{pole.name}</h3>
                    <span className={`pole-type-badge ${pole.pole_type}`}>
                      {poleTypeLabel(pole.pole_type)}
                    </span>
                  </div>

                  <div className="pole-card-content">
                    {/* Krabica = nadradený rozvodný splitter, ktorým idú káble ďalej */}
                    {vystupyRozvodne.length > 0 && (
                      <div className="pole-stat">
                        <span className="stat-icon">📦</span>
                        <span className="stat-label">Krabica:</span>
                        <span className="stat-value" style={{
                          fontWeight: 600,
                          color: obsadeneRozvodne === vystupyRozvodne.length ? '#b45309' : '#166534',
                        }}>
                          {obsadeneRozvodne}/{vystupyRozvodne.length}
                        </span>
                        {typyKrabice && (
                          <span className="splitter-typ-badge" style={
                            farbaKrabice ? { background: farbaKrabice } : undefined
                          }>
                            {typyKrabice}
                          </span>
                        )}
                      </div>
                    )}

                    {vystupy.length > 0 && (
                      <div className="pole-stat">
                        <span className="stat-icon">👤</span>
                        <span className="stat-label">Zákazníci:</span>
                        <span className="stat-value" style={{
                          fontWeight: 600,
                          color: obsadenych === 0 ? '#999'
                            : obsadenych === vystupy.length ? '#b45309' : '#166534',
                        }}>
                          {obsadenych}/{vystupy.length}
                        </span>
                      </div>
                    )}

                    <div className="pole-stat">
                      <span className="stat-icon">🔄</span>
                      <span className="stat-label">Priechod:</span>
                      <span className="stat-value">
                        {pole.cassette?.pass_through_cables?.length || 0}
                      </span>
                    </div>

                    <div className="pole-stat">
                      <span className="stat-icon">🔗</span>
                      <span className="stat-label">Zvary:</span>
                      <span className="stat-value">
                        {pole.cassette?.splices?.length || 0}
                      </span>
                    </div>

                    <div className="pole-stat">
                      <span className="stat-icon">📡</span>
                      <span className="stat-label">Splittery:</span>
                      <span className="stat-value">
                        {pole.cassette?.splitters?.length || 0}
                      </span>
                    </div>

                    <div className="pole-stat">
                      <span className="stat-icon">📸</span>
                      <span className="stat-label">Fotky:</span>
                      <span className="stat-value">
                        {pole.photos?.length || 0}
                      </span>
                    </div>
                  </div>

                  <div className="pole-card-footer">
                    <button
                      className="btn-view-details"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPole(pole);
                        setViewMode('pole-detail');
                      }}
                    >
                      Zobraziť detail →
                    </button>
                    <button
                      className="btn-photos"
                      onClick={(e) => handleOpenPhotos(pole, e)}
                      title="Zobraziť a pridať fotky"
                    >
                      📸 Fotky
                    </button>
                    <button
                      className="btn-print-report"
                      onClick={(e) => handleOpenTechnicalReport(pole, e)}
                      title="Tlačiť technickú správu"
                    >
                      🖨️ Správa
                    </button>
                  </div>
                </div>
                );
              })}
            </div>

            {polesWithCassettes.length === 0 && (
              <div className="empty-state">
                <p>Zatiaľ nie je kazeta nastavená na žiadnom stĺpiku.</p>
                <p>Pridajte kazety na stĺpiky a zobrazia sa tu.</p>
              </div>
            )}

            {polesWithCassettes.length > 0 && filteredPoles.length === 0 && (
              <div className="empty-state">
                <p>Žiadne výsledky pre "{searchQuery}"</p>
                <p>Skúste iný vyhľadávací výraz</p>
              </div>
            )}
          </div>
        )}

        {viewMode === 'pole-detail' && selectedPole && (
          <div className="pole-detail-view">
            <div className="detail-header">
              <button
                className="btn-back"
                onClick={() => {
                  setViewMode('pole-list');
                  setSelectedPole(null);
                }}
              >
                ← Späť na zoznam
              </button>
              <button className="btn-print" onClick={handleExportPDF}>
                🖨️ Tlačiť
              </button>
            </div>

            <CassetteDocumentation pole={selectedPole} cables={cables} poles={poles} />
          </div>
        )}
      </div>

      {reportPole && (
        <TechnicalReportDialog
          isOpen={showTechnicalReport}
          onClose={handleCloseTechnicalReport}
          pole={reportPole}
          cables={cables}
          poles={poles}
        />
      )}

      {photoPole && (
        <EditPoleDialog
          isOpen={showEditPoleDialog}
          onClose={handleClosePhotos}
          pole={photoPole}
          initialTab="photos"
        />
      )}
    </div>
  );
};

